import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { cn, formatBRL } from "@/lib/utils";
import type { SalesRepPerformance } from "@/data/types";

type Period = { periodStart: string; periodEnd: string };

type MedalKey = "gold" | "silver" | "bronze";

const MEDAL: Record<MedalKey, { icon: string; label: string; bg: string; ring: string; glow: string }> = {
  gold: {
    icon: "🥇",
    label: "Ouro",
    bg: "bg-gradient-to-br from-yellow-400/20 to-amber-600/10",
    ring: "ring-yellow-400/50",
    glow: "shadow-[0_0_40px_-10px_rgba(245,158,11,0.6)]",
  },
  silver: {
    icon: "🥈",
    label: "Prata",
    bg: "bg-gradient-to-br from-slate-300/20 to-slate-500/10",
    ring: "ring-slate-300/40",
    glow: "shadow-[0_0_30px_-12px_rgba(148,163,184,0.5)]",
  },
  bronze: {
    icon: "🥉",
    label: "Bronze",
    bg: "bg-gradient-to-br from-orange-400/20 to-amber-700/10",
    ring: "ring-orange-400/40",
    glow: "shadow-[0_0_24px_-12px_rgba(234,88,12,0.5)]",
  },
};

export function Top3PerformersModal({
  open,
  period,
  onClose,
}: {
  open: boolean;
  period: Period;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["sales-reps", period],
    queryFn: () => client.getSalesReps(period),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const top3 = useMemo(() => {
    const reps = q.data ?? [];
    return [...reps].sort((a, b) => b.amountInPeriod - a.amountInPeriod).slice(0, 3);
  }, [q.data]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />

      <div className="relative z-10 w-full max-w-3xl overflow-hidden rounded-xl border border-border-strong bg-bg-card shadow-2xl">
        <header className="flex items-start justify-between border-b border-border-subtle px-7 py-5">
          <div className="space-y-1">
            <div className="text-[11px] font-medium uppercase tracking-[0.15em] text-brand-amber">
              Top Performers do período
            </div>
            <h2 className="text-xl font-semibold tracking-tight text-text-primary">Pódio da equipe comercial</h2>
            <p className="text-[13px] text-text-muted">Ranking dos três vendedores que mais fecharam no período selecionado</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle text-lg text-text-secondary transition hover:border-border-strong hover:bg-bg-card-hover hover:text-text-primary"
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className="px-7 py-6">
          {q.isLoading && (
            <div className="py-12 text-center text-sm text-text-muted">Calculando ranking…</div>
          )}

          {!q.isLoading && top3.length === 0 && (
            <div className="py-12 text-center text-sm text-text-muted">
              Nenhum vendedor com vendas no período.
            </div>
          )}

          {top3.length > 0 && (
            <Podium top3={top3} />
          )}
        </div>
      </div>
    </div>
  );
}

function Podium({ top3 }: { top3: SalesRepPerformance[] }) {
  const medals: MedalKey[] = ["gold", "silver", "bronze"];
  // Ordem visual: prata à esquerda (2º), ouro ao centro (1º), bronze à direita (3º).
  const visualOrder = [1, 0, 2]
    .map((i) => ({ rep: top3[i], medal: medals[i] }))
    .filter((x) => !!x.rep);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-end">
      {visualOrder.map(({ rep, medal }) => (
        <PodiumCard key={rep!.userId} rep={rep!} medal={medal} />
      ))}
    </div>
  );
}

function PodiumCard({ rep, medal }: { rep: SalesRepPerformance; medal: MedalKey }) {
  const cfg = MEDAL[medal];
  const heightClass = medal === "gold" ? "md:min-h-[280px]" : medal === "silver" ? "md:min-h-[240px]" : "md:min-h-[210px]";
  const positionNum = medal === "gold" ? 1 : medal === "silver" ? 2 : 3;

  return (
    <div
      className={cn(
        "relative flex flex-col items-center gap-3 rounded-xl border border-border-subtle p-5 ring-1 transition",
        cfg.bg,
        cfg.ring,
        cfg.glow,
        heightClass
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-4xl" aria-hidden>{cfg.icon}</span>
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-text-muted">
          {positionNum}º · {cfg.label}
        </span>
      </div>

      <span
        className="flex h-16 w-16 items-center justify-center rounded-full text-lg font-bold text-text-on-accent"
        style={{ backgroundColor: rep.avatarColor }}
      >
        {getInitials(rep.name)}
      </span>

      <div className="space-y-1 text-center">
        <div className="font-semibold text-text-primary">{rep.name}</div>
        <div className="text-[11px] text-text-muted">
          Conversão {rep.conversionRate.toFixed(1).replace(".", ",")}%
        </div>
      </div>

      <div className="mt-auto w-full space-y-2 border-t border-border-subtle pt-3">
        <Row label="Vendas" value={rep.salesCount.toString()} />
        <Row label="Valor no período" value={formatBRL(rep.amountInPeriod)} accent />
        <Row label="Acumulado YTD" value={formatBRL(rep.amountAccumulated)} />
        <Row label="Reuniões" value={rep.meetingsHeld.toString()} />
      </div>
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-text-muted">{label}</span>
      <span className={cn("font-semibold", accent ? "text-brand-amber" : "text-text-primary")}>{value}</span>
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
